import { FC, Fragment, useState } from "react";
import { pad, stringToBytes, toBytes } from "viem";

import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import { useApi } from "@dzambalaorg/provider-api-firebase";
import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, ContractFeatures, type IContract, type IDiscrete, type IToken } from "@framework/types";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  convertTemplateToChainAsset,
} from "@framework/exchange";
import ExchangeGradeFacetUpgradeABI from "@framework/abis/json/ExchangeGradeFacet/upgrade.json";
import { ZeroAddress } from "@framework/constants";

import { getEthPrice, getMultiplier } from "./utils";
import { type IUpgradeDto, UpgradeDialog } from "./dialog";

interface IUpgradeButtonProps {
  className?: string;
  disabled?: boolean;
  token: IToken;
  variant?: ListActionVariant;
}

export const GradeButton: FC<IUpgradeButtonProps> = props => {
  const { className, disabled = false, token, variant = ListActionVariant.button } = props;

  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);

  const api = useApi();
  const { writeContractAsync } = useWriteContractWithSimulation();

  const { contractFeatures } = token.template!.contract!;

  const { execute: metaFnWithAllowance } = useAllowance(
    async (values: IUpgradeDto, discrete: IDiscrete, sign: IServerSignature, systemContract: IContract) => {
      const level = token.metadata[values.attribute] || 0;
      const item = convertTemplateToChainAsset(token.template, "1");
      const price = convertDatabaseAssetToChainAsset(discrete.price?.components, {
        multiplier: getMultiplier(level, discrete),
      });

      // TODO must fill real tokenID
      Object.assign(item, { tokenId: token.tokenId });

      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: ExchangeGradeFacetUpgradeABI,
        functionName: "upgrade",
        args: [
          {
            externalId: discrete.id,
            expiresAt: sign.expiresAt,
            nonce: toBytes(sign.nonce, { size: 32 }),
            extra: pad(stringToBytes(values.attribute), { size: 32 }),
            receiver: discrete.contract!.merchant!.wallet,
            referrer: ZeroAddress,
          },
          item,
          price,
          sign.signature,
          {
            value: getEthPrice(price),
          },
        ],
      });
    },
  );

  const metaFnWithSign = useServerSignature(
    (values: IUpgradeDto, sign: IServerSignature, systemContract: IContract) => {
      return api
        .fetchJson({
          url: "/discrete",
          data: {
            tokenId: token.id,
            attribute: values.attribute,
          },
        })
        .then((discrete: IDiscrete) => {
          const level = token.metadata[values.attribute] || 0;
          const price = convertDatabaseAssetToTokenTypeAsset(discrete.price?.components, {
            multiplier: getMultiplier(level, discrete),
          });

          return metaFnWithAllowance(
            { contract: systemContract.address, assets: price },
            values,
            discrete,
            sign,
            systemContract,
          );
        });
    },
  );

  const { execute: metaFn } = useMetamask((values: IUpgradeDto) => {
    return metaFnWithSign(
      {
        url: "/discrete/sign",
        method: "POST",
        data: {
          tokenId: token.id,
          attribute: values.attribute,
        },
      },
      values,
    ) as Promise<void>;
  });

  const handleUpgrade = (): void => {
    setIsUpgradeDialogOpen(true);
  };

  const handleUpgradeConfirm = async (dto: IUpgradeDto): Promise<void> => {
    await metaFn(dto).then(() => {
      setIsUpgradeDialogOpen(false);
    });
  };

  const handleUpgradeCancel = (): void => {
    setIsUpgradeDialogOpen(false);
  };

  if (!contractFeatures.includes(ContractFeatures.DISCRETE)) {
    return null;
  }

  return (
    <Fragment>
      <ListAction
        onClick={handleUpgrade}
        message="form.buttons.upgrade"
        className={className}
        dataTestId="ExchangeUpgradeButton"
        disabled={disabled}
        variant={variant}
      />
      <UpgradeDialog
        onCancel={handleUpgradeCancel}
        onConfirm={handleUpgradeConfirm}
        open={isUpgradeDialogOpen}
        initialValues={{
          attribute: "",
          contractId: token.template!.contractId,
        }}
      />
    </Fragment>
  );
};
